import { useCallback, useEffect, useState } from "react";
import { Eye, EyeOff, ImagePlus, Pencil, Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api, { API_BASE, formatApiError } from "@/lib/api";

const EMPTY = { title: "", description: "", image_path: null, link_url: "", is_active: true };

export default function BannerPanel({ toast }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await api.get("/admin/banners");
            setItems(res.data);
        } catch (err) {
            toast({
                title: "Gagal memuat banner",
                description: formatApiError(err),
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    }, [toast]);

    useEffect(() => {
        load();
    }, [load]);

    const startNew = () => {
        setEditing("new");
        setForm(EMPTY);
        setFile(null);
        setPreview(null);
    };

    const startEdit = (b) => {
        setEditing(b.id);
        setForm({
            title: b.title || "",
            description: b.description || "",
            image_path: b.image_path,
            link_url: b.link_url || "",
            is_active: b.is_active,
        });
        setFile(null);
        setPreview(`${API_BASE}/files/${b.image_path}`);
    };

    const cancel = () => {
        setEditing(null);
        setForm(EMPTY);
        setFile(null);
        setPreview(null);
    };

    const onFileChange = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setFile(f);
        setPreview(URL.createObjectURL(f));
    };

    const save = async () => {
        if (!file && !form.image_path) {
            toast({
                title: "Pilih gambar banner dulu",
                description: "Ukuran terbaik: 1200 x 400 px (rasio 3:1).",
                variant: "destructive",
            });
            return;
        }
        setSaving(true);
        try {
            let image_path = form.image_path;
            if (file) {
                const fd = new FormData();
                fd.append("file", file);
                const up = await api.post("/admin/banner-photo", fd);
                image_path = up.data.path;
            }
            const payload = {
                title: form.title.trim() || null,
                description: form.description.trim() || null,
                link_url: form.link_url.trim() || null,
                is_active: form.is_active,
                image_path,
            };
            if (editing === "new") {
                await api.post("/admin/banners", payload);
                toast({ title: "Banner ditambahkan" });
            } else {
                await api.patch(`/admin/banners/${editing}`, payload);
                toast({ title: "Banner diperbarui" });
            }
            cancel();
            load();
        } catch (err) {
            toast({
                title: "Gagal menyimpan banner",
                description: formatApiError(err),
                variant: "destructive",
            });
        } finally {
            setSaving(false);
        }
    };

    const remove = async (b) => {
        if (!window.confirm("Hapus banner ini?")) return;
        try {
            await api.delete(`/admin/banners/${b.id}`);
            toast({ title: "Banner dihapus" });
            load();
        } catch (err) {
            toast({
                title: "Gagal menghapus banner",
                description: formatApiError(err),
                variant: "destructive",
            });
        }
    };

    const toggleActive = (b) => {
        const upd = async () => {
            try {
                await api.patch(`/admin/banners/${b.id}`, { is_active: !b.is_active });
                load();
            } catch (err) {
                toast({
                    title: "Gagal mengubah status banner",
                    description: formatApiError(err),
                    variant: "destructive",
                });
            }
        };
        upd();
    };

    return (
        <div data-testid="banners-panel">
            {editing ? (
                <div
                    data-testid="banner-form"
                    className="rounded-3xl border border-border bg-white p-8 shadow-brand"
                >
                    <h3 className="font-display text-xl font-extrabold text-ink">
                        {editing === "new" ? "Tambah Banner Promo" : "Ubah Banner Promo"}
                    </h3>

                    <div className="mt-6 space-y-5">
                        <div className="grid gap-5 sm:grid-cols-2">
                            <div className="space-y-2">
                                <Label className="text-sm font-bold text-ink/80">
                                    Judul (opsional)
                                </Label>
                                <Input
                                    data-testid="banner-input-title"
                                    value={form.title}
                                    onChange={(e) =>
                                        setForm((f) => ({ ...f, title: e.target.value }))
                                    }
                                    placeholder="Contoh: PROMO PELANGGAN BARU"
                                    maxLength={60}
                                    className="h-12 rounded-xl border-ink/10 bg-[#F9FAFB] focus-visible:ring-brand"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-sm font-bold text-ink/80">
                                    Link tujuan (opsional)
                                </Label>
                                <Input
                                    data-testid="banner-input-link"
                                    value={form.link_url}
                                    onChange={(e) =>
                                        setForm((f) => ({ ...f, link_url: e.target.value }))
                                    }
                                    placeholder="https://wa.me/6285715550050..."
                                    className="h-12 rounded-xl border-ink/10 bg-[#F9FAFB] focus-visible:ring-brand"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-sm font-bold text-ink/80">
                                Deskripsi (opsional)
                            </Label>
                            <Textarea
                                data-testid="banner-input-description"
                                rows={2}
                                value={form.description}
                                onChange={(e) =>
                                    setForm((f) => ({ ...f, description: e.target.value }))
                                }
                                placeholder="Contoh: Diskon 20% untuk laundry kiloan pertama Anda"
                                maxLength={120}
                                className="rounded-xl border-ink/10 bg-[#F9FAFB] focus-visible:ring-brand"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label className="text-sm font-bold text-ink/80">
                                Gambar Banner{" "}
                                <span className="font-medium text-ink/40">
                                    (terbaik 1200 x 400 px, rasio 3:1, maks. 5 MB)
                                </span>
                            </Label>
                            <label className="flex h-12 cursor-pointer items-center gap-2 rounded-xl border border-ink/10 bg-[#F9FAFB] px-4 text-sm font-semibold text-ink/60 transition-colors hover:border-brand hover:text-brand">
                                <ImagePlus size={17} />
                                {file ? file.name : "Pilih gambar..."}
                                <input
                                    data-testid="banner-input-image"
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp,image/gif"
                                    className="hidden"
                                    onChange={onFileChange}
                                />
                            </label>
                        </div>

                        {preview && (
                            <div className="space-y-2">
                                <img
                                    src={preview}
                                    alt="Pratinjau banner"
                                    data-testid="banner-photo-preview"
                                    className="aspect-[3/1] w-full rounded-xl object-cover ring-1 ring-border"
                                />
                                <p className="text-xs text-ink/40">
                                    Pratinjau — di HP banner ditampilkan penuh
                                    tanpa terpotong, teks berpindah ke bawah
                                    gambar.
                                </p>
                            </div>
                        )}

                        <div className="flex flex-wrap items-center gap-3">
                            <Button
                                data-testid="banner-save-button"
                                onClick={save}
                                disabled={saving}
                                className="h-11 rounded-full bg-brand px-8 font-bold text-white hover:bg-brand-deep disabled:opacity-60"
                            >
                                {saving ? "Menyimpan..." : "Simpan"}
                            </Button>
                            <Button
                                data-testid="banner-cancel-button"
                                variant="outline"
                                onClick={cancel}
                                className="h-11 rounded-full border-ink/10 font-bold text-ink hover:bg-brand-mist/50 hover:text-brand"
                            >
                                Batal
                            </Button>
                            <button
                                data-testid="banner-active-toggle"
                                type="button"
                                onClick={() =>
                                    setForm((f) => ({ ...f, is_active: !f.is_active }))
                                }
                                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold transition-colors ${
                                    form.is_active
                                        ? "bg-emerald-100 text-emerald-700"
                                        : "bg-slate-200 text-slate-600"
                                }`}
                            >
                                {form.is_active ? <Eye size={14} /> : <EyeOff size={14} />}
                                Status: {form.is_active ? "Aktif" : "Nonaktif"}
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    <div className="flex justify-end">
                        <Button
                            data-testid="banner-add-button"
                            onClick={startNew}
                            className="h-11 rounded-full bg-brand px-6 font-bold text-white hover:bg-brand-deep"
                        >
                            <Plus size={17} className="mr-1.5" />
                            Tambah Banner
                        </Button>
                    </div>

                    {loading ? (
                        <p className="mt-6 py-14 text-center text-sm text-ink/50">
                            Memuat banner...
                        </p>
                    ) : items.length === 0 ? (
                        <div
                            data-testid="banners-empty"
                            className="mt-6 rounded-3xl border border-border bg-white py-14 text-center shadow-brand"
                        >
                            <p className="font-display text-lg font-bold text-ink">
                                Belum ada banner promo
                            </p>
                            <p className="mx-auto mt-1 max-w-md text-sm text-ink/50">
                                Tambahkan banner promo — ukuran terbaik
                                1200 x 400 px (rasio 3:1), format JPG/PNG/WebP
                                maksimal 5 MB.
                            </p>
                        </div>
                    ) : (
                        <div className="mt-6 grid gap-5 md:grid-cols-2">
                            {items.map((b) => (
                                <div
                                    key={b.id}
                                    data-testid={`admin-banner-${b.id}`}
                                    className="flex flex-col rounded-3xl border border-border bg-white p-6 shadow-brand"
                                >
                                    <img
                                        src={`${API_BASE}/files/${b.image_path}`}
                                        alt={b.title || "Banner"}
                                        className="aspect-[3/1] w-full rounded-xl object-cover"
                                    />
                                    <div className="mt-4 flex-1">
                                        <p className="font-display font-bold text-ink">
                                            {b.title || "Tanpa judul"}
                                        </p>
                                        {b.description && (
                                            <p className="mt-1 text-sm text-ink/60">
                                                {b.description}
                                            </p>
                                        )}
                                        {b.link_url && (
                                            <p className="mt-1 truncate text-xs text-brand" title={b.link_url}>
                                                {b.link_url}
                                            </p>
                                        )}
                                    </div>
                                    <div className="mt-4 flex flex-wrap gap-2 border-t border-border pt-4">
                                        <span
                                            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${
                                                b.is_active
                                                    ? "bg-emerald-100 text-emerald-700"
                                                    : "bg-slate-200 text-slate-600"
                                            }`}
                                        >
                                            {b.is_active ? <Eye size={12} /> : <EyeOff size={12} />}
                                            {b.is_active ? "Aktif" : "Nonaktif"}
                                        </span>
                                        <Button
                                            data-testid={`banner-toggle-${b.id}`}
                                            size="sm"
                                            variant="outline"
                                            onClick={() => toggleActive(b)}
                                            className="rounded-full border-ink/10 text-xs font-bold text-ink hover:bg-brand-mist/50 hover:text-brand"
                                        >
                                            {b.is_active ? "Nonaktifkan" : "Aktifkan"}
                                        </Button>
                                        <Button
                                            data-testid={`banner-edit-${b.id}`}
                                            size="sm"
                                            variant="outline"
                                            onClick={() => startEdit(b)}
                                            className="rounded-full border-ink/10 text-xs font-bold text-ink hover:bg-brand-mist/50 hover:text-brand"
                                        >
                                            <Pencil size={13} className="mr-1" />
                                            Ubah
                                        </Button>
                                        <Button
                                            data-testid={`banner-delete-${b.id}`}
                                            size="sm"
                                            variant="outline"
                                            onClick={() => remove(b)}
                                            className="rounded-full border-ink/10 text-xs font-bold text-ink hover:bg-red-50 hover:text-red-600"
                                        >
                                            <Trash2 size={13} className="mr-1" />
                                            Hapus
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
